import { getMainPageRestrictionRedirect, hasMainPageRestriction } from '../../../services/accessManagement/mainPageRestriction';
import type { BackendUser } from '../../../services/user/userService';
import { canAccessInventoryPage, isInvoiceOnlyAccount } from '../../../services/accessManagement/accessManagement';
import { tourLocation } from '../tourLocation';
import type { OnboardingTour } from '../types';
import { dashboardTour } from './dashboard';
import { ordersTour } from './pages';
import { revenueTour } from './revenue';
import { costPriceTour } from './costPrice';
import { utilizationTour } from './utilization';
import { inventoryTour } from './inventory';
import { leakageTour } from './leakage';

const TOURS: Record<string, OnboardingTour> = {
  dashboard: dashboardTour,
  orders: ordersTour,
  revenue: revenueTour,
  costPrice: costPriceTour,
  utilization: utilizationTour,
  inventory: inventoryTour,
  leakage: leakageTour,
};

export function getTour(id: string): OnboardingTour | undefined {
  return TOURS[id];
}

export function listTours(): OnboardingTour[] {
  return Object.values(TOURS);
}

/** Routes with supplied steps, including read-only lists reached during a tour.
 * Never advertise replay on an unrelated editor with an unsaved draft.
 */
function tourRoutes(tour: OnboardingTour): string[] {
  return [...tour.paths, ...tour.steps, ...Object.values(tour.branches ?? {}).flat()]
    .map(entry => typeof entry === 'string' ? entry : entry.route)
    .filter((route): route is string => Boolean(route));
}

/** Only overview roots auto-start; contextual replay also covers declared list steps. */
export function getTourForPath(pathname: string, options: { includeSubpages?: boolean; search?: string } = {}): OnboardingTour | undefined {
  const location = tourLocation(pathname, options.search);
  if (location.startsWith('/inventory?')) {
    if (location.endsWith('tab=history')) return undefined;
    return location.endsWith('tab=variance') ? leakageTour : inventoryTour;
  }
  return listTours().find(tour => (options.includeSubpages ? tourRoutes(tour) : tour.paths).includes(pathname));
}

export function tourOwnsPath(tour: OnboardingTour, pathname: string): boolean {
  const location = tourLocation(pathname);
  if (location.startsWith('/inventory?')) {
    return (tour.id === 'inventory' && location.endsWith('tab=review')) ||
      (tour.id === 'leakage' && location.endsWith('tab=variance'));
  }
  if (tour.ownedRoutePrefixes?.some(prefix => pathname.split('?')[0].startsWith(prefix))) return true;
  return tourRoutes(tour).includes(pathname);
}

/** The supplied tours belong to the regular subscribed dashboard, including approved staff. */
export function canAccessTour(tour: OnboardingTour, user: BackendUser | null, accountSettings: string[]): boolean {
  return Boolean(TOURS[tour.id] && user &&
    !getMainPageRestrictionRedirect((tour.entryRoute ?? tour.paths[0]).split('?')[0], user, accountSettings) &&
    (!['inventory', 'leakage'].includes(tour.id) || canAccessInventoryPage(user, accountSettings)) &&
    !(user.role === 'staff' && (user.access_request === 'pending' || user.access_request === 'rejected')) &&
    (hasMainPageRestriction(user) || !isInvoiceOnlyAccount(user, accountSettings)));
}
